import { useState } from "react";
import { ApiError } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";

/** KEY-822: the CalDAV providers offered by name, and where each makes its app passwords. "other" takes an address. */
export const CALDAV_PRESETS = [
  {
    key: "icloud",
    label: "Apple iCloud",
    passwordHelp: "Use an app-specific password, not your Apple ID password. Make one at account.apple.com → Sign-In and Security → App-Specific Passwords.",
    helpUrl: "https://support.apple.com/en-gb/102654",
  },
  {
    key: "fastmail",
    label: "Fastmail",
    passwordHelp: "Use an app password with calendar access, from Fastmail's Settings → Privacy & Security.",
    helpUrl: "https://www.fastmail.help/hc/en-us/articles/360058752854-App-passwords",
  },
  {
    key: "yahoo",
    label: "Yahoo",
    passwordHelp: "Use an app password from your Yahoo Account Security page.",
    helpUrl: "https://help.yahoo.com/kb/SLN15241.html",
  },
  {
    key: "other",
    label: "Another CalDAV server",
    passwordHelp: "Your provider's CalDAV address, and an app password if it offers them.",
    helpUrl: null,
  },
] as const;

export type CalDavPreset = (typeof CALDAV_PRESETS)[number]["key"];

/** KEY-822: what the server is sent as `server` — the preset's name, or the typed address for "other". */
export function caldavServer(preset: CalDavPreset, address: string): string {
  return preset === "other" ? address.trim() : preset;
}

/**
 * KEY-822: sign in to Apple iCloud or another CalDAV account, in Connected calendars. Apple has no consent screen to
 * send anyone to, so this is the one place a password is typed — an app-specific one, which the person can revoke
 * from Apple's side at any time. The password field is cleared whatever the answer, and is never shown again.
 */
export function CalDavConnectForm({ field, onAdded }: { field: React.CSSProperties; onAdded: (message: string) => void }) {
  const [preset, setPreset] = useState<CalDavPreset>("icloud");
  const [address, setAddress] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chosen = CALDAV_PRESETS.find((p) => p.key === preset)!;
  const ready = username.trim() !== "" && password !== "" && (preset !== "other" || address.trim() !== "");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = getAccessToken();
    if (!token || !ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      const view = await api.addCalDavCalendar(token, caldavServer(preset, address), username.trim(), password, label.trim() || null);
      setUsername("");
      setLabel("");
      setAddress("");
      onAdded(`${view.accountLabel} connected${view.calendarNames?.length ? `: ${view.calendarNames.join(", ")}` : ""}.`);
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : "That account couldn't be connected.");
    } finally {
      setPassword("");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} aria-label="Connect Apple iCloud or another CalDAV calendar" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label htmlFor="caldav-preset" style={{ fontSize: 13, color: "var(--text-muted)" }}>
        Apple iCloud, Fastmail, Yahoo or another CalDAV calendar
      </label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <select id="caldav-preset" value={preset} onChange={(e) => setPreset(e.target.value as CalDavPreset)} style={{ ...field, flex: "1 1 160px" }}>
          {CALDAV_PRESETS.map((p) => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
        </select>
        {preset === "other" && (
          <input
            aria-label="CalDAV server address"
            type="url"
            inputMode="url"
            placeholder="https://dav.example.com/"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            style={{ ...field, flex: "2 1 220px" }}
          />
        )}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          aria-label="Account email or username"
          autoComplete="username"
          placeholder="Email or username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          style={{ ...field, flex: "2 1 200px" }}
        />
        <input
          aria-label="App-specific password"
          type="password"
          autoComplete="new-password"
          placeholder="App-specific password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{ ...field, flex: "2 1 200px" }}
        />
        <input
          aria-label="Name for this account"
          placeholder="Name (optional)"
          value={label}
          maxLength={200}
          onChange={(e) => setLabel(e.target.value)}
          style={{ ...field, flex: "1 1 140px" }}
        />
        <button type="submit" className="kd-btn kd-btn-sm" disabled={busy || !ready}>
          {busy ? "Signing in…" : "Connect"}
        </button>
      </div>
      <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
        {chosen.passwordHelp}{" "}
        {chosen.helpUrl && (
          <a href={chosen.helpUrl} target="_blank" rel="noreferrer">How to make one</a>
        )}
      </div>
      {error && <div role="alert" style={{ fontSize: 13, color: "var(--danger, #b42318)" }}>{error}</div>}
    </form>
  );
}
